import { sortedIndexBy, merge, pick } from 'lodash'
import { NavigationNode, ContentNode, SearchContentNode, LayoutContentNode } from 'models'
import { emiRequestFactory } from './emiRequest'
import { EmiRequest, Deeplink, Tile, InternalLink } from 'models'
import VoltError from 'VoltError'
import config from 'services/emi/config'
import { resolveTemplateType } from 'services/emi/helpers'

import { getLabelsInTwoChar } from '../helpers'
import { virtualChannelFactory } from './emiVirtualChannels'

const getHighlightTemplate = (template) => {
    const ratio = config.barkerRatio[template]
    return {
        type: resolveTemplateType(template),
        numOfBarkers: config.highlightNumberOfBarkers[template],
        ratio: {
            landscape: ratio.landscape || ratio,
            portrait: ratio.portrait || ratio,
        },
    }
}

/**
 * Return an array of RootPages configuration for all active and future programmings.
 *
 * @param {EmiProgramming} programmings
 * @param {String} moduleRootId identifier of root node of this module (ex: `root-coach`, `root-vod`)
 * @param {Number} [ageLimit] when provided used to exclude potentially "too old" programming (even if still technically active)
 * @returns {Array<EmiRootPage>}
 */
const getRootPages = (programmings, moduleRootId, ageLimit) => {
    const currentDateTime = Date.now()
    // start_epoch = timestamp which indicate when this programmation must be active
    // Ensure provided programming are sorted in chronological "activation date"
    const sortedProgrammings = programmings.sort((a, b) => a.start_epoch - b.start_epoch)

    // index will be used to identify which programming should be currently active
    // (ie: by using a `index - 1`)
    const index = sortedIndexBy(
        sortedProgrammings,
        { start_epoch: Date.now() / 1000 },
        'start_epoch'
    )

    programmings = programmings
        // slice = Exclude outdated programming (which was planned before the currently active)
        .slice(Math.max(index - 1))
        // filter = apply eventual ageLimit parameter
        .filter((programming) => {
            if (!ageLimit) return true

            // good to know: age could be < 0 in case of future programmings
            const age = currentDateTime - programming.start_epoch * 1000

            return age <= ageLimit
        })

    if (!programmings.length) {
        throw new VoltError(VoltError.codes.INVALID_EMI_CONFIG, {
            extraLog: `${moduleRootId}: No active EMI configuration identified`,
        })
    }

    return programmings.map((programming) => ({
        startTime: programming.start_epoch * 1000,
        page: programming.pages[0],
        trees: programming.trees,
        programmingId: programming?.id,
    }))
}

/**
 * @param {Object} params
 * @param {String} params.moduleRootId ex: `root-coach` or `root-vod`
 * @param {String} params.rootPageName page name of the root entry point
 * @param {Object<String, EmiPage>} params.pages indexed by page name
 * @param {Object<String, EmiFilterPPZ|EmiFilterSPZ|EmiFilterMenu>} params.filters indexed by filter name
 * @param {Object<String, EmiRequest>} params.requests indexed by request name
 * @param {Object<Boolean} [params.translateRoot=true] Indicates whether the label of the module root should be translated
 */
const buildNavMap = ({
    moduleRootId,
    rootPageName,
    pages,
    filters,
    requests: requestByEmiId,
    translateRoot = true,
}) => {
    //Sometimes in filters defined in EMI doesn't have a parent or an associated root. Let's call them orphan filters
    const orphanFilters = !Object.values(filters).some((filter) => filter.type === 'menu')

    const rootMenuFilter = orphanFilters
        ? { name: rootPageName, parent: 'root' }
        : Object.values(filters).find((filter) => filter.child_name === rootPageName)

    const rootId = orphanFilters ? rootPageName : moduleRootId

    const navMap = {}
    // Requests are indexed by an ID computed with resolveEmiRequestId (from services/emi/helpers)
    const requestMap = {}

    const buildGrid = (children, pageLayout, color, pageDetailedView) => {
        return children.reduce(
            (acc, id) => {
                const child = filters[id]
                if (!child) return acc
                const request = requestByEmiId[child.request.name]

                const requestProps = {
                    labels: child && child.labels && getLabelsInTwoChar(child.labels),
                    id: request?.id,
                    emiUniqueId: child.request.name,
                    icon:
                        child.media &&
                        child.media.logo &&
                        Object.values(child.media.logo).length &&
                        Object.values(child.media.logo)[0],
                    channelImageType: child.channel_image_type,
                }

                if (child.type === 'promotional') {
                    const template = getHighlightTemplate(child.line_template)
                    const overrideType =
                        child.line_template === 'dvr-storage' ||
                        child.line_template === 'wallet-info'

                    acc.highlight.type = overrideType ? child.line_template : template.type
                    acc.highlight.numOfBarkers = template.numOfBarkers
                    acc.highlight.ratio = template.ratio.landscape
                    acc.highlight.ratioPortrait = template.ratio.portrait
                    acc.highlight.carousel_auto_delay = child.carousel_auto_delay
                    requestProps.context = EmiRequest.CONTEXT.PPZ

                    if (child.line_template === 'info' || overrideType) {
                        const infoBarker = request.args?.[0] // an info barker only has one item

                        if (infoBarker) {
                            acc.highlight.infoBarker = infoBarker.thumbnail || infoBarker.backdrop
                            acc.highlight.portraitInfoBarker = infoBarker.portraitThumbnail
                        }
                    } else {
                        acc.highlight.data.push(requestProps)
                        requestMap[request?.id] = request
                    }
                } else if (child.type === 'filter' || child.type === 'filterGenerator') {
                    // Although 'filter generator' model has been introduced for smartui,
                    // it is not necessary for volt product yet as framework are not the same.
                    // So a filter generator yet acts as a 'filter' until we find it necessary (used yet only for Channel Apps)
                    // Not necessary to store "ContentSelector" object added for a 'filterGenerator'
                    const bgColor = color

                    acc.body.type =
                        request.layout === 'table'
                            ? request.layout
                            : pageLayout === 'wall'
                            ? 'wall'
                            : 'list'
                    acc.body.hasDetailView = !!pageDetailedView
                    acc.body.backgroundColor = bgColor
                    requestProps.context = EmiRequest.CONTEXT.SPZ
                    acc.body.data.push(requestProps)
                    requestMap[request?.id] = request
                }
                return acc
            },
            {
                highlight: { type: null, data: [] },
                body: { type: null, data: [], backgroundColor: null },
            }
        )
    }

    const builder = (id, parent) => {
        // id is equals to rootId for the first recursion or for orphan filters, on this case associated filter
        // should be the one identified as the rootMenuFilter.
        const filter = filters[id === rootId ? rootMenuFilter.name : id]

        const pageId = orphanFilters ? rootId : filter.child_name
        const page = pageId && pages[pageId]

        const children = (page && page.filters) || null

        const node = {
            icon: filter && filter.media && filter.media.logo,
            id, // id is either the moduleRootId or the real filter name
            labels: filter && filter.labels && getLabelsInTwoChar(filter.labels),
            parent,
            jump_group_id: filter?.jump_group_id,
            translateLabel: id === moduleRootId,
        }

        let labelExists
        if (node.labels && Object.keys(node.labels).length) {
            if (Object.keys(node.labels).length === 1) {
                labelExists = Object.keys(node.labels)[0] !== 'default'
            } else {
                labelExists = true
            }
        }

        if (translateRoot && id === moduleRootId) {
            node.intlKey = !labelExists && { tag: moduleRootId }
        }

        if (children) {
            if (page.type === 'content' || page.type === 'filter' || page.type === 'search') {
                const color = filter && filter.color
                const { highlight, body } = buildGrid(
                    children,
                    page.layout,
                    color,
                    page.detailed_view
                )

                const bodyDataFromFilter = pick(filter, [
                    'media',
                    'text_color',
                    'interactive_text_color',
                    'interactive_background_color',
                ])

                if (page.type === 'search') {
                    navMap[id] = new SearchContentNode({
                        ...node,
                        highlight,
                        body: {
                            ...body,
                            ...bodyDataFromFilter,
                        },
                    })
                } else if (page.type === 'profiles') {
                    navMap[id] = new LayoutContentNode({
                        ...node,
                        highlight,
                        body: {
                            ...body,
                            ...bodyDataFromFilter,
                        },
                        layout: 'profiles',
                    })
                } else {
                    navMap[id] = new ContentNode({
                        ...node,
                        highlight,
                        body: {
                            ...body,
                            ...bodyDataFromFilter,
                        },
                    })
                }
            } else {
                navMap[id] = new NavigationNode({
                    ...node,
                    children,
                })
                children.forEach((child) => builder(child, id))
            }
        }
    }

    builder(rootId, rootMenuFilter.parent)

    return { navMap, requestMap }
}

const buildApps = (emiApplications, platform) => {
    if (emiApplications.length === 0) return []
    return emiApplications.reduce((acc, application) => {
        const appProps = {
            id: application?.id,
            title: application.name,
            description: application.long_description,
            thumbnail: application.thumbnails[0],
            format: Tile.FORMAT.LANDSCAPE,
        }

        switch (application.type) {
            case config.applicationType.internalLink: {
                acc.push(
                    new InternalLink({
                        ...appProps,
                        path: application.command.split('/').pop(),
                    })
                )
                break
            }

            case config.applicationType.androidApplication: {
                if (platform === 'WebOs') {
                    acc.push(
                        new Deeplink({
                            ...appProps,
                            command: application?.smart_tv_webos?.command,
                            param: application?.smart_tv_webos?.param,
                        })
                    )
                } else if (platform === 'Tizen') {
                    acc.push(
                        new Deeplink({
                            ...appProps,
                            command: application?.smart_tv_tizen?.command,
                            param: application?.smart_tv_tizen?.param,
                        })
                    )
                } else {
                    acc.push(
                        new Deeplink({
                            ...appProps,
                            command: application.command,
                            param: application.param,
                        })
                    )
                }
                break
            }

            default:
        }

        return acc
    }, [])
}

/**
 * Parse EMI Configuration an returns all data linked to universe ( allPages, allFilters, allRequests....).
 *
 * @param {EmiModuleData} moduleData Module configuration
 * @param {Object} appConfig configuration, provided by the Consumer Project
 * @param {Array} [rootPageNames] names of root pages
 * @returns {EmiUniverseData}
 */
const parseEmiUniverseData = (moduleData, appConfig, rootPageNames = []) => {
    const allPages = moduleData.pages.reduce((acc, node) => {
        acc[node.name] = node
        return acc
    }, {})

    const allFilters = moduleData.filters.reduce((acc, node) => {
        if (rootPageNames.includes(node.child_name)) {
            node.labels = node.labels || moduleData.moduleRoot.labels
            node.parent = moduleData.moduleRoot.parent
        }

        acc[node.name] = node
        return acc
    }, {})

    const applications =
        moduleData.catalogs &&
        moduleData.catalogs.applications &&
        moduleData.catalogs.applications.list
            ? buildApps(moduleData.catalogs.applications.list, appConfig?.deviceData?.deviceType)
            : []

    // All requests indexed by unique EMI ID (contains all requests from all programmings retrieved)
    const allRequestByEmiId = moduleData.request_providers.reduce((acc, request) => {
        acc[request.name] = emiRequestFactory(request, appConfig, applications)
        return acc
    }, {})

    return { allPages, allFilters, applications, allRequestByEmiId }
}

/**
 * Parse EMI Configuration an returns navigation map and request map.
 *
 * @param {EmiFullConfig} emiFullConfig the full EMI Config (expect `moduleRoot` attr added by us in `moduleData`)
 * @param {Number} [ageLimit] eventual age limit checked against programmation `start_epoch` to deduce root page
 * @param {Object} appConfig configuration, provided by the Consumer Project
 * @returns {EmiModuleConfiguration}
 */
export const parseEmiConfig = (emiFullConfig, ageLimit, appConfig) => {
    const { moduleData } = emiFullConfig
    const rootPages = getRootPages(moduleData.programmings, moduleData.moduleRoot?.id, ageLimit)
    const rootPageNames = rootPages.map((x) => x.page)

    const { allPages, allFilters, applications, allRequestByEmiId } = parseEmiUniverseData(
        moduleData,
        appConfig,
        rootPageNames
    )

    const programmingsConfig = rootPages.reduce((acc, rootPage) => {
        const navAndRequest = buildNavMap({
            moduleRootId: moduleData.moduleRoot?.id,
            rootPageName: rootPage.page,
            pages: allPages,
            filters: allFilters,
            requests: allRequestByEmiId,
        })

        const treesData = (rootPage.trees || []).reduce((acc, name) => {
            const tree = moduleData.trees.find((t) => t.name === name)
            const treePage = tree && tree.page

            if (!treePage) return acc

            const treeNavMap = buildNavMap({
                moduleRootId: treePage,
                rootPageName: treePage,
                pages: allPages,
                filters: allFilters,
                requests: allRequestByEmiId,
                translateRoot: false,
            })

            return merge(acc, treeNavMap)
        }, {})

        acc[rootPage.startTime] = {
            ...merge(navAndRequest, treesData),
            // useful to expose the programming identifier which leads to this config
            programmingId: rootPage.programmingId,
            applications: applications,
        }
        return acc
    }, {})
    return {
        programmings: programmingsConfig,
        polling: emiFullConfig.config,
        revision: emiFullConfig.revision,
    }
}

/**
 * Parse EMI Configuration an returns navigation map and request map.
 *
 * @param {EmiFullConfig} emiFullConfig the full EMI Config (expect `moduleRoot` attr added by us in `moduleData`)
 * @param {Number} [ageLimit] eventual age limit checked against programmation `start_epoch` to deduce root page
 * @param {Object} appConfig configuration, provided by the Consumer Project
 * @returns {EmiAppdockProgrammings}
 */
export const parseEmiAppdockConfig = (emiFullConfig, ageLimit, appConfig) => {
    const { moduleData } = emiFullConfig

    const { allPages, allFilters, applications, allRequestByEmiId } = parseEmiUniverseData(
        moduleData,
        appConfig
    )

    const programmingsConfig = moduleData.configurations.reduce((acc, configuration) => {
        const programings = configuration.programmings.reduce((acc, programing) => {
            const navAndRequest = buildNavMap({
                moduleRootId: '',
                rootPageName: programing.page,
                pages: allPages,
                filters: allFilters,
                requests: allRequestByEmiId,
            })

            const currentPrograming = {
                ...navAndRequest,
                // useful to expose the programming identifier which leads to this config
                programmingId: programing.emiId,
                applications: applications,
                param: programing.param,
                fromTimestamp: programing.fromTimestamp,
                toTimestamp: programing.toTimestamp,
            }
            if (!acc[programing.context]) acc[programing.context] = {}
            acc[programing.context][programing.type]
                ? acc[programing.context][programing.type].push(currentPrograming)
                : (acc[programing.context][programing.type] = [currentPrograming])
            return acc
        }, {})
        acc[configuration.sourceChannelId] = programings
        return acc
    }, {})

    return {
        programmings: programmingsConfig,
        polling: emiFullConfig.config,
        revision: emiFullConfig.revision,
    }
}

export const parseEmiVirtualChannelsConfig = (emiFullConfig, ageLimit, appConfig) => {
    const { moduleData } = emiFullConfig

    const programmingsConfig = moduleData.channels.reduce((allChannelsAcc, channel) => {
        const lcn = channel.lcn
        const sourceChannelId = channel.sourceChannelId
        const name = channel.name
        const addInServicePlan = channel.add_in_service_plan
        const zappingFromChannelList = channel.launchChannelFromChannelList
        const logo = channel.logo
        const programings = channel.programmings.reduce((acc, programing) => {
            const virtualChannel = virtualChannelFactory(
                {
                    ...programing,
                    lcn,
                    sourceChannelId,
                    name,
                    addInServicePlan,
                    zappingFromChannelList,
                    logo,
                },
                appConfig
            )
            //convert timestamp to ms
            acc[programing.fromTimestamp * 1000] = {
                virtualChannel,
                // useful to expose the programming identifier which leads to this config
                // not given by EMI, so create it
                programmingId:
                    emiFullConfig.revision +
                    '_' +
                    sourceChannelId +
                    '_' +
                    programing.fromTimestamp * 1000,
            }
            return acc
        }, {})
        allChannelsAcc[channel.sourceChannelId] = programings
        return allChannelsAcc
    }, {})

    return {
        programmings: programmingsConfig,
        polling: emiFullConfig.config,
        revision: emiFullConfig.revision,
    }
}

/**
 * @typedef {Object} EmiParsedConfig
 * @property {EmiModulesConfiguration} modules Configuration of the differents modules/universes
 * @property {NavigationNode} root The root navigation node which "rule them all" !
 */

/**
 * @typedef {Object} EmiModulesConfiguration
 * @property {EmiModuleConfiguration} vod VOD module configurations per startTime
 * @property {EmiModuleConfiguration} home Home/Coach module configurations per startTime
 * @property {EmiThemeModuleConfiguration} theme theme module configurations per startTime
 */

/**
 * @typedef {Object} EmiModuleConfiguration
 * @property {EmiModuleProgrammings} programmings
 * @property {EmiPolling} [polling] polling configuration, missing when programmings have been generated from a non-emi source (ex: VOD config with EMI Vod universe disabled, proxy node used)
 * @property {Number} [revision] the revision number of the file which has been parsed to provide programmings, missing when issing when programmings have been generated from a non-emi source
 */

/**
 * @typedef {Object<String, EmiModuleProgramming>} EmiModuleProgrammings
 * @description Indexed by timestamp. Refers to startTime of programming availability
 */

/**
 * @typedef {Object} EmiModuleProgramming
 * @property {Number} programmingId
 * @property {Object<String, ContentNode|NavigationNode>} navMap Flattened navigation map indexed by their node id.
 *      NavigationNode refers to other NavigationNode/ContentNode (by name).
 *      ContentNode refers to EmiRequest contained in `requestMap`.
 * @property {Object<String, EmiRequest>} requestMap all requests used to retrieve/display content, indexed by their id.
 *      For a {@link EmiRequest} built from {@link EmiModuleDataConfig} the `name` is used as identifier.
 *      For custom {@link EmiRequest} a constant was used as identifier.
 * @applications {Array<Object>} applications list of all emi applications defined for this programming
 */

/**
 * @typedef {Object} EmiThemeModuleConfiguration
 * @property {EmiThemeModuleProgrammings} programmings
 * @property {EmiPolling} [polling] polling configuration, missing when programmings have been generated from a non-emi source (ex: VOD config with EMI Vod universe disabled, proxy node used)
 * @property {Number} [revision] the revision number of the file which has been parsed to provide programmings, missing when issing when programmings have been generated from a non-emi source
 */

/**
 * @typedef {Object<String, EmiThemeModuleProgramming>} EmiThemeModuleProgrammings
 * @description Indexed by timestamp. Refers to startTime of programming availability
 */

/**
 * @typedef {Object} EmiThemeModuleProgramming
 * @property {Number} programmingId
 * @property {EmiThemeData} theme Flattened navigation map indexed by their node id.
 */

/**
 * @typedef {Object<String, EmiAppdockContext>} EmiAppdockProgrammings
 * @description Indexed by channels. Refers to channel lcn
 */

/**
 * @typedef {Object<String, EmiAppdockModuleProgramming} EmiAppdockContext
 * @description Indexed by Context. Refers to current state of player: live, dvr, replay ...
 */

/**
 * @typedef {Object} EmiAppdockModuleProgramming
 * @property {Number} programmingId
 * @property {Object<String, ContentNode|NavigationNode>} navMap Flattened navigation map indexed by their node id.
 *      NavigationNode refers to other NavigationNode/ContentNode (by name).
 *      ContentNode refers to EmiRequest contained in `requestMap`.
 * @property {Object<String, EmiRequest>} requestMap all requests used to retrieve/display content, indexed by their id.
 *      For a {@link EmiRequest} built from {@link EmiModuleDataConfig} the `name` is used as identifier.
 *      For custom {@link EmiRequest} a constant was used as identifier.
 * @applications {Array<Object>} applications list of all emi applications defined for this programming
 * @property {EmiTimeslotCondition|EmiEPGCondition>} param display conditions
 */

/**
 * @typedef EmiTimeslotCondition
 * @description used to define weekday and hours when this emi config should be applied
 * @property {String} name timeslot
 * @property {Array<String>} days refers to the weekdays when config should be applied
 * @property {Array<String>} hours defines timeslot when config should be applied
 */

/**
 * @typedef EmiEPGCondition
 * @description used to define an epg when this emi config should be applied
 * @property {String} programDate timeslot
 * @property {String} programId refers to the weekdays when config should be applied
 * @property {String} programName defines timeslot when config should be applied
 * @property {String} programThumbnail timeslot
 * @property {String} programTime refers to the weekdays when config should be applied
 * @property {String} recurrence defines timeslot when config should be applied
 */

/**
 * The full EMI Theme data retrieved from server
 *
 * @typedef {Object} EmiThemeData
 * @property {String} [backgroundImage] 16/9 image
 * @property {String} [backgroundSideMenuImage] Vertical menu image
 * @property {String} [focusColor] hexa color #rrggbb
 * @property {String} [launchImage] image to be used during boot
 * @property {String} [locale] language
 * @property {String} [topImage] client logo image
 */

/**
 * The full EMI configuration retrieved from server
 *
 * @typedef {Object} EmiFullConfig
 * @property {String} [apiVersion] unused for Volt projects
 * @property {String} [builtTimeStamp] format =YYYYMMDD hh:mm:ss, unused for Volt projects
 * @property {EmiPolling} config Polling configuration
 * @property {String} [device] unused for Volt projects
 * @property {String} [emiModule] unused for Volt projects
 * @property {String} [license] unused for Volt projects
 * @property {EmiModuleData} moduleData Module configuration
 * @property {String} [population] unused for Volt projects
 * @property {Number} revision Revision file number which provided the configurations
 */

/**
 * @typedef {Object} EmiPolling
 * @property {Number} [bootCheckDelay] in minutes, unused for Volt projects
 * @property {Number} [bootDelay] in minutes, unused for Volt projects
 * @property {Boolean} [checkOnBoot] unused for Volt projects
 * @property {Number} [minPullingDelay] in minutes, unused for Volt projects
 * @property {Number} periodicDelay in minutes, delay between two pollings
 * @property {String} [retryDelays] Comma separated numbers, unsed for Volt projects
 */

/**
 * @typedef {Object} EmiModuleData
 * @property {String} anchor WTF is that ? not used here
 * @property {NavigationNode} moduleRoot the root NavigationNode of the module, injected by us
 * @property {Array<EmiProgramming>} programmings
 * @property {EmiCatalogs} catalogs
 * @property {Array<EmiFilterPPZ|EmiFilterSPZ|EmiFilterMenu>} filters
 * @property {Array<EmiPage>} pages
 * @property {Array<EmiRequestProvider>} request_providers
 */

/**
 * @typedef {Object} EmiUniverseData
 * @property {Array<EmiFilterPPZ|EmiFilterSPZ|EmiFilterMenu>} allFilters
 * @property {Array<EmiPage>} allPages
 * @property {Array<EmiRequestProvider>} allRequestByEmiId
 * @property {Array<Object>} applications list of all emi applications defined for this programming
 */

/**
 * @typedef {Object} EmiProgramming
 * @property {Number} id EMI programming identifer (probably auto-increment)
 * @property {Array<String>} pages Entry point of the programmation. Refers to objects in {@link EmiModuleDataConfig} (`pages`) through `name` (NOT the `id`).
 *      Is an array of string but we only consider the first one. (Dunno why it is an array)
 * @property {Number} start_epoch Unix timestamp (UTC) from which programmation should be enabled/displayed
 * @property {String} type Dunno ¯\_(ツ)_/¯ it seems always equals to `'default'`
 */

/**
 * @typedef {Object} EmiCatalogs
 * @property {EmiCatalog} applications
 * @property {EmiCatalog} stores
 */

/**
 * @typedef {Object} EmiPage
 * @property {Number} id EMI page identifier (probably auto-increment)
 * @property {String} name EMI page name (auto-generated) IMPORTANT: objects relation seems
 *      always done using this name.
 * @property {String} type one of `content` ... TBC
 * @property {String} entry_filter refers to a filter name, probably used on TV/STB to auto-focus the right filter
 * @property {Array<String>} filters Array of filters name contained in this page
 */

/**
 * @typedef {Object} EmiRequestProvider
 * @property {Number} id EMI Request Provider identifier (probably auto-increment)
 * @property {String} name EMI Request Provider name (auto-generated) IMPORTANT: objects relation seems
 *      always done using this name.
 * @property {Array<EmiItem|EmiPreDefinedItem>} items Items in this request, when pre-defined
 *      request is used it could be only a single item here: a {@link EmiPreDefinedItem}.
 */

/**
 * @typedef {Object} EmiFilterPPZ
 * @property {Number} id EMI filter identifier (probably auto-increment)
 * @property {String} name EMI filter name (auto-generated) IMPORTANT: objects relation seems
 *      always done using this name.
 * @property {Object<String, String>} labels Key-Value pair. Key = language code. Value = the translation
 * @property {String} line_template one of `info`, `carousel`, `duo`, `duoHalf`, `tertio`,
 *      `tertioHalf`, `quatro`, `quatroHalf`, `barker`
 * @property {EmiRequest} request associated request to the filter
 * @property {String} type Equals to `promotional`
 */

/**
 * @typedef {Object} EmiFilterSPZ
 * @property {Number} id EMI filter identifier (probably auto-increment)
 * @property {Object<String, String>} labels Key-Value pair. Key = language code. Value = the translation
 * @property {String} name name of the filter (auto-generated) IMPORTANT: objects relation seems
 *      always done using this name.
 * @property {EmiRequest} request
 * @property {String} type Equals to `filter`
 */

/**
 * @typedef {Object} EmiFilterMenu
 * @property {Number} id EMI filter identifier (probably auto-increment)
 * @property {String} name name of the filter (auto-generated) IMPORTANT: objects relation seems
 *      always done using this name.
 * @property {String} type Equals to `menu`
 * @property {String} child_name No idea on the purpose of this attribute, seems to refers to a {@link EmiModuleDataConfig} (`pages`) by name
 */

/**
 * @typedef {Object} EmiCatalog
 * @property {Array<*>} list Seems always empty
 * @property {String} type Seems always `'full'`
 */

/**
 * @typedef {Object} EmiRequest
 * @property {String} provider seems always equals to `emi`
 * @property {String} name refers to an object in `request_providers`
 */

/**
 * @typedef {Object} EmiItem
 * @property {String} dataset (ex: `sclocal`) this will override default dataset defined in config
 * @property {String|Number} id platform item identifier
 * @property {String} layout one of `portrait` or `landscape` (<-- to confirm)
 * @property {Object} [media] Only defined when asset override is done from EMI
 * @property {Array<String>} media.thumbnails Array of image urls representing the item
 * @property {String} type one of `vod`, `channel`, `app` (app type is filtered out during parsing)
 */

/**
 * @typedef {Object} EmiPreDefinedItem
 * @property {Object} data
 * @property {String} data.name The content selector name (ex = `content_genre`, `content_favorites`)
 * @property {String} [data.category] Only for `data.name==='content_genre'` & 'data.name='Playlist' (Youtube). The category label to use to retrieve content
 * @property {String} [data.node] Only for EMI request for Nodes display. Unfortunately EMI does not provide a data.name (will ask to be fixed with name = content_node)
 * @property {String} layout one of `portrait` or `landscape` (<-- to confirm)
 * @property {String} provider The content selector provider (free text) (ex= `VODContent`, `PVRContent`, `Youtube`, `ChannelApplication`)
 * @property {String} provider_type one of `vod` ... TBC
 * @property {String} type seems always equals to `collection`
 */

/**
 * @typedef {Object} EmiRootPage
 * @property {Number} startTime milliseconds timestamp (from `start_epoch` of a {@link EmiProgramming})
 * @property {String} page The page name of the root page for a programming
 * @property {Number} programmingId the programming identifier
 */
